import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import type { Player } from '../lib/firebase/collections';

interface GameOverProps {
  players: Player[];
  onRestart: () => void;
  onReturnToLobby: () => void;
}

// Konfetti-Komponente
const Confetti = () => {
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, color: string, delay: number}>>([]);

  useEffect(() => {
    const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4'];
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 3
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute w-2 h-2 sm:w-3 sm:h-3 rounded-full"
          style={{
            backgroundColor: particle.color,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
          }}
          initial={{ scale: 0, y: -100, rotate: 0 }}
          animate={{ 
            scale: [0, 1, 0], 
            y: [0, 200, 400], 
            rotate: [0, 180, 360],
            opacity: [1, 1, 0]
          }}
          transition={{ 
            duration: 3,
            delay: particle.delay,
            repeat: Infinity,
            repeatDelay: 2
          }}
        />
      ))}
    </div>
  );
};

// Krone-Icon für den Gewinner
const CrownIcon = () => (
  <motion.svg 
    className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 mx-auto mb-2" 
    fill="currentColor" 
    viewBox="0 0 24 24"
    initial={{ scale: 0, rotate: -180 }}
    animate={{ scale: 1, rotate: 0 }}
    transition={{ delay: 0.5, type: "spring", bounce: 0.6 }}
  >
    <path d="M12 6L9 9l3-8 3 8z"/>
    <path d="M4 9l3-3 2 8H4z"/>
    <path d="M20 9l-3-3-2 8h5z"/>
    <path d="M4 14h16v2c0 1-1 2-2 2H6c-1 0-2-1-2-2v-2z"/>
  </motion.svg>
);

export default function GameOver({ players, onRestart, onReturnToLobby }: GameOverProps) {
  // Sortiere Spieler nach Punkten
  const sortedPlayers = [...players].sort((a, b) => b.score - a.score);
  const winner = sortedPlayers[0];

  return (
    <>
      <Confetti />
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-[#10181f] to-[#1a202c] flex items-center justify-center p-3 sm:p-4 font-sans overflow-y-auto">
        <motion.div
          className="relative w-full max-w-3xl mx-auto rounded-2xl sm:rounded-3xl bg-black/30 backdrop-blur-2xl border border-white/10 shadow-2xl overflow-hidden my-4"
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", bounce: 0.3 }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 via-blue-500/20 to-purple-500/20 pointer-events-none" />
          
          <div className="relative p-4 sm:p-6 md:p-8">
            {/* Header */}
            <motion.div 
              className="text-center mb-6 sm:mb-8 md:mb-12"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 mb-2 sm:mb-4 font-display tracking-tight break-words">
                SPIEL BEENDET!
              </h1>
              <div className="w-16 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mx-auto" />
            </motion.div>

            {/* Winner Section */}
            <motion.div 
              className="mb-6 sm:mb-8 md:mb-12"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <div className="text-center mb-4 sm:mb-6">
                <CrownIcon />
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">🏆 GEWINNER 🏆</h2>
              </div>
              
              <motion.div
                className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-xl border border-yellow-400/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 text-center relative overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 to-orange-400/10 animate-pulse" />
                
                <motion.p 
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-yellow-200 mb-1 sm:mb-2 relative break-words"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6, type: "spring", bounce: 0.5 }}
                >
                  {winner.name}
                </motion.p>
                <motion.p 
                  className="text-sm sm:text-base md:text-lg lg:text-xl text-yellow-300/80 relative"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  mit <span className="font-bold text-lg sm:text-xl md:text-2xl text-yellow-200">{winner.score}</span> Punkten
                </motion.p>
              </motion.div>
            </motion.div>

            {/* Leaderboard */}
            <motion.div 
              className="mb-6 sm:mb-8 md:mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-center mb-3 sm:mb-4 md:mb-6 text-white">🏅 ENDSTAND 🏅</h3>
              <div className="space-y-2 sm:space-y-3 max-h-60 sm:max-h-80 overflow-y-auto">
                {sortedPlayers.map((player, index) => {
                  const isWinner = index === 0;
                  const isSecond = index === 1;
                  const isThird = index === 2;
                  
                  let bgGradient = 'from-gray-800/50 to-gray-700/50';
                  let borderColor = 'border-gray-600/30';
                  let textColor = 'text-white';
                  let rankEmoji = '🔸';
                  
                  if (isWinner) {
                    bgGradient = 'from-yellow-600/30 to-orange-600/30';
                    borderColor = 'border-yellow-400/40';
                    textColor = 'text-yellow-200';
                    rankEmoji = '🥇';
                  } else if (isSecond) {
                    bgGradient = 'from-gray-400/30 to-gray-500/30';
                    borderColor = 'border-gray-400/40';
                    textColor = 'text-gray-200';
                    rankEmoji = '🥈';
                  } else if (isThird) {
                    bgGradient = 'from-amber-600/30 to-orange-700/30';
                    borderColor = 'border-amber-500/40';
                    textColor = 'text-amber-200';
                    rankEmoji = '🥉';
                  }

                  return (
                    <motion.div
                      key={player.uid}
                      className={`flex items-center justify-between p-3 sm:p-4 rounded-lg sm:rounded-xl bg-gradient-to-r ${bgGradient} backdrop-blur-xl border ${borderColor} hover:scale-105 transition-all duration-300`}
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 min-w-0 flex-1">
                        <span className="text-lg sm:text-xl md:text-2xl flex-shrink-0">{rankEmoji}</span>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm sm:text-base md:text-lg font-bold ${textColor} truncate`}>
                            {player.name}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-400">
                            Platz #{index + 1}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-lg sm:text-xl md:text-2xl font-black ${textColor}`}>
                          {player.score}
                        </span>
                        <p className="text-xs sm:text-sm text-gray-400">Punkte</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div 
              className="flex flex-col gap-3 sm:gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
            >
              <motion.button
                onClick={onRestart}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 sm:py-4 px-4 sm:px-6 md:px-8 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base md:text-lg shadow-lg hover:shadow-green-500/30 transition-all duration-300"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(34, 197, 94, 0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                🎮 Neues Spiel starten
              </motion.button>

              <motion.button
                onClick={onReturnToLobby}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-3 sm:py-4 px-4 sm:px-6 md:px-8 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base md:text-lg shadow-lg hover:shadow-gray-500/30 transition-all duration-300"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(107, 114, 128, 0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                🏠 Zurück zur Lobby
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </>
  );
} 